import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { clips, type Clip, duracionLabels, formatearNumero } from "@/mocks/clips";
import TvCarousel from "@/pages/tv-app/components/TvCarousel";

export default function TvVideoPage() {
  const navigate = useNavigate();
  const { videoId } = useParams<{ videoId: string }>();
  const [clip, setClip] = useState<Clip | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [focusedBtn, setFocusedBtn] = useState<string>("play");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showReplay, setShowReplay] = useState(false);

  const buttons = ["back", "replay", "play", "fullscreen", "related"] as const;

  // Load clip
  useEffect(() => {
    if (!videoId) return;
    const numericId = Number(videoId);
    if (isNaN(numericId)) return;
    const found = clips.find((c) => c.id === numericId);
    if (found) setClip(found);
  }, [videoId]);

  // Progress simulation
  useEffect(() => {
    if (!isPlaying || !clip) return;
    const totalMs = clip.duracionSegundos * 1000;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 500 / totalMs;
        if (next >= 1) {
          setIsPlaying(false);
          setShowReplay(true);
          return 1;
        }
        return next;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [isPlaying, clip]);

  const togglePlay = useCallback(() => {
    if (showReplay) {
      setProgress(0);
      setShowReplay(false);
      setIsPlaying(true);
      return;
    }
    setIsPlaying((prev) => !prev);
  }, [showReplay]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!clip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    setProgress(ratio);
    setShowReplay(false);
  }, [clip]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  // Get related clips
  const relatedClips = clip
    ? clips.filter((c) => c.id !== clip.id).slice(0, 5)
    : [];

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setProgress((prev) => Math.min(prev + 0.1, 1));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setProgress((prev) => Math.max(prev - 0.1, 0));
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (isFullscreen) {
          document.exitFullscreen().catch(() => {});
          setIsFullscreen(false);
        } else {
          navigate("/tv-app");
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "play" || focusedBtn === "replay") togglePlay();
        else if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "fullscreen") toggleFullscreen();
        else if (focusedBtn === "related") {
          const relatedSection = document.querySelector('[data-tv-section="related-videos"]');
          relatedSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, togglePlay, toggleFullscreen, isFullscreen]);

  // Not found
  if (!clip) {
    return (
      <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6">
            <i className="ri-film-line text-white/40 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Video no encontrado</h1>
          <p className="text-white/40 text-sm mb-8">Este clip no existe o fue eliminado.</p>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white">
            <i className="ri-arrow-left-line mr-2"></i>Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const durInfo = duracionLabels[clip.duracion];
  const progressPct = `${(progress * 100).toFixed(1)}%`;
  const elapsed = Math.floor(progress * clip.duracionSegundos);
  const elapsedMin = Math.floor(elapsed / 60);
  const elapsedSec = elapsed % 60;
  const totalMin = Math.floor(clip.duracionSegundos / 60);
  const totalSec = clip.duracionSegundos % 60;

  return (
    <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={clip.imagenUrl} alt="" className="w-full h-full object-cover object-top opacity-20 blur-xl scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/70 to-[#0a0a0f]/40"></div>
      </div>

      <div className="relative z-10 h-screen flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-6 lg:px-10 py-5">
          <button
            onClick={() => navigate("/tv-app")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${
              focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Volver
          </button>
          <div className="flex items-center gap-3">
            {clip.esTendencia && (
              <span className="px-3 py-1.5 rounded-full bg-rose-500/90 text-white text-xs font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                TENDENCIA
              </span>
            )}
            {durInfo && (
              <span className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold">{durInfo.label}</span>
            )}
          </div>
        </div>

        {/* Video Area */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-16">
          <div className="w-full max-w-5xl">
            {/* Thumbnail / Video Area */}
            <div className="relative w-full aspect-video rounded-3xl overflow-hidden bg-foreground-950/50 ring-1 ring-white/5 shadow-2xl">
              <img src={clip.imagenUrl} alt={clip.titulo} className="w-full h-full object-cover" />

              {/* Play overlay */}
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/30">
                  <button
                    onClick={togglePlay}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all cursor-pointer outline-none ${
                      focusedBtn === "play" || focusedBtn === "replay"
                        ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-110"
                        : "bg-white/90 text-[#0a0a0f] hover:bg-white hover:scale-105"
                    }`}
                  >
                    <i className={`text-2xl ${showReplay ? "ri-restart-line" : "ri-play-fill ml-1"}`}></i>
                  </button>
                </div>
              )}

              {/* Playing indicator */}
              {isPlaying && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                  Reproduciendo
                </div>
              )}
            </div>

            {/* Progress Bar — clickable */}
            <div className="mt-3 px-1">
              <div
                className="w-full h-2 rounded-full bg-white/10 cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div className="h-full bg-accent-400 rounded-full transition-all duration-300" style={{ width: progressPct }}></div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-white/50">{elapsedMin}:{String(elapsedSec).padStart(2, "0")}</span>
                <span className="text-xs text-white/50">{totalMin}:{String(totalSec).padStart(2, "0")}</span>
              </div>
            </div>

            {/* Video Info */}
            <div className="mt-4">
              <h1 className="text-2xl lg:text-3xl font-bold text-white">{clip.titulo}</h1>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="flex items-center gap-1.5 text-sm text-white/60">
                  <img src={clip.radioLogo} alt="" className="w-5 h-5 rounded-full object-cover" />
                  {clip.radioNombre}
                </span>
                {clip.locutorNombre && (
                  <span className="text-sm text-white/40 flex items-center gap-1">
                    <i className="ri-user-voice-line"></i>
                    {clip.locutorNombre}
                  </span>
                )}
                {clip.programaNombre && (
                  <span className="text-sm text-white/40 flex items-center gap-1">
                    <i className="ri-mic-line"></i>
                    {clip.programaNombre}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-3">
                <span className="text-sm text-white/40 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatearNumero(clip.vistas)} vistas
                </span>
                <span className="text-sm text-white/40 flex items-center gap-1">
                  <i className="ri-share-forward-line"></i>
                  {formatearNumero(clip.compartidos)} compartidos
                </span>
              </div>
              <p className="text-sm text-white/50 mt-4 max-w-2xl leading-relaxed">{clip.descripcion}</p>
            </div>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="flex items-center justify-center gap-3 px-6 lg:px-16 py-6">
          {/* Back */}
          <button
            onClick={() => navigate("/tv-app")}
            className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${
              focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Volver
          </button>

          {/* Replay */}
          <button
            onClick={() => { setProgress(0); setShowReplay(false); setIsPlaying(true); }}
            className={`flex items-center gap-1 px-4 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "replay" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-restart-line text-lg"></i>
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className={`flex items-center gap-3 px-10 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-bold ${
              focusedBtn === "play" || focusedBtn === "replay"
                ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105"
                : isPlaying ? "bg-white text-[#0a0a0f] hover:bg-white/90" : "bg-white/80 text-[#0a0a0f] hover:bg-white"
            }`}
          >
            <i className={`text-xl ${isPlaying ? "ri-pause-fill" : "ri-play-fill"}`}></i>
            {isPlaying ? "Pausar" : showReplay ? "Repetir" : "Reproducir"}
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className={`flex items-center gap-1 px-4 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "fullscreen" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className={`text-lg ${isFullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"}`}></i>
          </button>
        </div>

        {/* Related Content */}
        {relatedClips.length > 0 && (
          <div data-tv-section="related-videos" className="px-6 lg:px-10 pb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <i className="ri-film-line text-accent-400"></i>
              Videos relacionados
            </h3>
            <TvCarousel>
              {relatedClips.map((c) => (
                <button
                  key={c.id}
                  tabIndex={0}
                  onClick={() => { setProgress(0); setIsPlaying(false); setShowReplay(false); navigate(`/tv-app/video/${c.id}`); }}
                  className="flex-shrink-0 w-[240px] text-left cursor-pointer outline-none focus:ring-2 focus:ring-white rounded-xl transition-all"
                >
                  <div className="aspect-video rounded-xl overflow-hidden bg-foreground-950/50">
                    <img src={c.imagenUrl} alt={c.titulo} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <p className="text-sm font-semibold text-white mt-2 line-clamp-2">{c.titulo}</p>
                  <p className="text-xs text-white/40 mt-1">{c.radioNombre}</p>
                </button>
              ))}
            </TvCarousel>
          </div>
        )}
      </div>
    </div>
  );
}